import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Preview, PreviewAction} from 'platform/components';
import {Box, Text, Show} from 'platform/foundation';
import {Lightbox, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetPreviewFromSettingsQuery} from '@dms/api/core';
import {UpdateWatermarkRequestBody, UpdateWatermarkV2RequestBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {buildArray} from 'shared/web';

interface WatermarkDetailPreviewProps {
  watermarkData: UpdateWatermarkRequestBody | UpdateWatermarkV2RequestBody;
  /**
   * Composition applied in this session, rendered on the sample photo. It takes precedence over the
   * settings preview, whose `fileId` still points to the previously saved render (or to nothing).
   */
  compositionPreviewUrl?: string | null;
  /**
   * The form holds a composition that differs from the saved one. The settings preview then shows
   * the previous render, so it is not used: only a fresh composition render is shown, else empty.
   */
  isCompositionUnsaved?: boolean;
  /** The applied composition is being composed onto the sample photo. */
  isComposingPreview?: boolean;
}

// Large enough to read the watermark on the sample photo; 3:2 like the photo itself.
const PREVIEW_WIDTH = 120;
const PREVIEW_HEIGHT = 80;

export function WatermarkDetailPreview(props: WatermarkDetailPreviewProps) {
  const shouldUseSettingsPreview =
    isNil(props.compositionPreviewUrl) &&
    !props.isCompositionUnsaved &&
    isNotNil(props.watermarkData.fileId);

  const {data, isLoading, isFetching, isError} = useGetPreviewFromSettingsQuery(
    shouldUseSettingsPreview ? props.watermarkData : skipToken
  );

  const base64Url = shouldUseSettingsPreview ? data?.base64Url : props.compositionPreviewUrl;

  const [lightboxControls, {onOpen}] = useLightbox('watermarkDetailPreview');
  const lightboxActionsFactory = useLightboxActionsFactory();

  const handleOpen = () => {
    onOpen(0);
  };

  return (
    <>
      <Text color="secondary" size="xSmall">
        {i18n.t('entity.watermark.labels.preview')}
      </Text>
      <Box height={PREVIEW_HEIGHT} width={PREVIEW_WIDTH}>
        <DataStatus
          minHeight="100%"
          isLoading={isLoading || isFetching || props.isComposingPreview}
          isError={isError}
          isEmpty={isNil(base64Url)}
        >
          <>
            <Preview
              url={base64Url ?? ''}
              width={PREVIEW_WIDTH}
              height={PREVIEW_HEIGHT}
              makeUrl={() => base64Url ?? ''}
              shouldUseOriginal
              fit="contain"
              actions={buildArray<PreviewAction>().add({
                icon: 'action/visibility',
                onClick: handleOpen,
              })}
              data-testid={testIds.vehicles.photos('watermarkDetailPreview')}
            />
            <Show when={isNotNilOrEmpty(base64Url)}>
              <Lightbox
                data-testid={testIds.vehicles.photos('watermarkDetailPreview')}
                controls={lightboxControls}
                images={lightboxActionsFactory.addFullActions([
                  {
                    id: 'watermarkDetailPreview',
                    url: base64Url?.toString()?.replace('resize', 'get') ?? '',
                  },
                ])}
              />
            </Show>
          </>
        </DataStatus>
      </Box>
    </>
  );
}
